import { API_BASE_URL } from './globals';

/** Minimal attachment info needed to match cid: references. */
type InlineAttachment = {
  id: string;
  /** Content-ID header value associated with the attachment (if provided by Nylas) */
  contentId?: string;
  /** Filename (optional fallback match) */
  name?: string;
};

type RewriteCidImagesParams = {
  html: string;
  attachments?: InlineAttachment[];
  grantId?: string;
  messageId?: string;
  token?: string;
};

export type RewriteCidImagesResult = {
  html: string;
  /** Number of cid: image sources found in the HTML */
  cidImageCount: number;
  /** Number of images successfully rewritten to API URLs */
  replacedCount: number;
};

/**
 * Safely decode a URI component, returning the original value if decoding fails.
 * Handles malformed percent-encoded sequences gracefully.
 */
function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Normalize a CID reference (e.g., `cid:<image001.png@abc>`) or Content-ID header
 * value into a stable lowercase comparison key.
 *
 * Handles:
 * - `cid:` prefix removal
 * - URI decoding
 * - Surrounding angle brackets `<...>`
 *
 * @example
 * normalizeCid('cid:<Image001.PNG@ABC>') // => 'image001.png@abc'
 */
function normalizeCid(value: string): string {
  let v = value.trim();

  // Remove cid: prefix if present
  if (v.toLowerCase().startsWith('cid:')) {
    v = v.slice('cid:'.length).trim();
  }

  v = safeDecodeURIComponent(v);

  // Strip surrounding angle brackets (may be nested in some edge cases)
  while (v.startsWith('<') && v.endsWith('>')) {
    v = v.slice(1, -1).trim();
  }

  return v.toLowerCase();
}

/**
 * Build an absolute URL to our `/nylas/attachments/:id` endpoint
 * with required query params for authorization and inline display.
 */
function buildInlineAttachmentUrl(params: {
  attachmentId: string;
  grantId: string;
  messageId: string;
  token: string;
}): string {
  const { attachmentId, grantId, messageId, token } = params;

  const url = new URL(`/nylas/attachments/${encodeURIComponent(attachmentId)}`, API_BASE_URL);

  url.searchParams.set('grantId', grantId);
  url.searchParams.set('messageId', messageId);
  url.searchParams.set('token', token);
  url.searchParams.set('inline', 'true');

  return url.toString();
}

/**
 * Build a lookup index from attachments for fast CID matching.
 * Keys are normalized contentId and/or filename (both lowercased).
 */
function buildAttachmentIndex(attachments: InlineAttachment[]): Map<string, InlineAttachment> {
  const map = new Map<string, InlineAttachment>();

  for (const att of attachments) {
    // Index by normalized contentId (primary match)
    if (att.contentId) {
      const key = normalizeCid(att.contentId);
      if (key)
        map.set(key, att);
    }

    // Also index by filename as fallback.
    // Useful when HTML uses `cid:image001.png` but Nylas didn't return a contentId.
    if (att.name) {
      const filenameKey = att.name.trim().toLowerCase();
      if (filenameKey)
        map.set(filenameKey, att);
    }
  }

  return map;
}

/**
 * Parse an img src attribute to extract CID info.
 *
 * @returns Object with `full` (complete normalized cid) and optional `base`
 *          (part before `@` for Outlook-style cids like `image001.png@01DC...`).
 *          Returns null if src is not a cid: reference.
 */
function getCidFromImgSrc(src: string): { full: string; base?: string } | null {
  if (!src)
    return null;

  const trimmed = src.trim();
  if (!trimmed.toLowerCase().startsWith('cid:'))
    return null;

  const full = normalizeCid(trimmed);
  if (!full)
    return null;

  // Outlook-style CIDs have format: `image001.png@01DC556F.1A818960`
  // Extract base (filename) for fallback matching when full CID doesn't match.
  const atIndex = full.indexOf('@');
  const base = atIndex > 0 ? full.slice(0, atIndex) : undefined;

  return { full, base };
}

/**
 * Rewrite cid: images using DOMParser (browser environment).
 * More reliable than regex for complex/malformed HTML.
 */
function rewriteUsingDomParser(params: {
  html: string;
  attachmentIndex: Map<string, InlineAttachment>;
  grantId: string;
  messageId: string;
  token: string;
}): RewriteCidImagesResult {
  const { html, attachmentIndex, grantId, messageId, token } = params;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const imgs = [...doc.querySelectorAll('img[src]')];

  let cidImageCount = 0;
  let replacedCount = 0;

  for (const img of imgs) {
    const rawSrc = img.getAttribute('src') ?? '';
    const cid = getCidFromImgSrc(rawSrc);
    if (!cid)
      continue;

    cidImageCount++;

    // Try full CID match first, then fall back to base (filename) match
    const match = attachmentIndex.get(cid.full) ?? (cid.base ? attachmentIndex.get(cid.base) : undefined);
    if (!match)
      continue;

    img.setAttribute(
      'src',
      buildInlineAttachmentUrl({
        attachmentId: match.id,
        grantId,
        messageId,
        token,
      }),
    );
    replacedCount++;
  }

  return {
    html: replacedCount > 0 ? doc.body.innerHTML : html,
    cidImageCount,
    replacedCount,
  };
}

/**
 * Regex-based fallback for non-browser environments (e.g., tests without jsdom).
 * Only targets `<img ... src="cid:..." ...>` patterns.
 */
function rewriteUsingRegex(params: {
  html: string;
  attachmentIndex: Map<string, InlineAttachment>;
  grantId: string;
  messageId: string;
  token: string;
}): RewriteCidImagesResult {
  const { html, attachmentIndex, grantId, messageId, token } = params;

  let cidImageCount = 0;
  let replacedCount = 0;

  // Regex captures: (1) prefix before quote, (2) quote char, (3) cid:value, (4) closing quote
  const rewrittenHtml = html.replaceAll(
    /(<img\b[^>]*?\ssrc\s*=\s*)(["'])(cid:[^"']+)(\2)/gi,
    (_match, prefix: string, quote: string, srcValue: string, suffix: string) => {
      const cid = getCidFromImgSrc(srcValue);
      if (!cid)
        return `${prefix}${quote}${srcValue}${suffix}`;

      cidImageCount++;

      // Try full CID match first, then fall back to base (filename) match
      const match = attachmentIndex.get(cid.full) ?? (cid.base ? attachmentIndex.get(cid.base) : undefined);
      if (!match)
        return `${prefix}${quote}${srcValue}${suffix}`;

      replacedCount++;

      const newSrc = buildInlineAttachmentUrl({
        attachmentId: match.id,
        grantId,
        messageId,
        token,
      });

      return `${prefix}${quote}${newSrc}${suffix}`;
    },
  );

  return {
    html: replacedCount > 0 ? rewrittenHtml : html,
    cidImageCount,
    replacedCount,
  };
}

/**
 * Rewrites inline `<img src="cid:...">` references to absolute URLs pointing
 * to our `/nylas/attachments/:id` API endpoint.
 *
 * Matching strategy:
 * 1. Try exact match on full normalized CID (e.g., `image001.png@01dc...`)
 * 2. Fall back to base filename match (e.g., `image001.png`) for Outlook-style CIDs
 *
 * @returns Result with rewritten HTML and counts for debugging.
 */
export function rewriteCidImagesToNylasAttachments(params: RewriteCidImagesParams): RewriteCidImagesResult {
  const { html, attachments, grantId, messageId, token } = params;

  // Early exit if missing required params
  if (!html || !attachments?.length || !grantId || !messageId || !token) {
    return { html, cidImageCount: 0, replacedCount: 0 };
  }

  const attachmentIndex = buildAttachmentIndex(attachments);
  if (attachmentIndex.size === 0) {
    return { html, cidImageCount: 0, replacedCount: 0 };
  }

  try {
    // Use DOMParser in browser for reliable parsing; regex fallback for Node/tests
    if (globalThis.DOMParser !== undefined)
      return rewriteUsingDomParser({ html, attachmentIndex, grantId, messageId, token });

    return rewriteUsingRegex({ html, attachmentIndex, grantId, messageId, token });
  } catch {
    return { html, cidImageCount: 0, replacedCount: 0 };
  }
}

/**
 * Extract all cid: references from HTML for debugging/logging purposes.
 * @returns Array of raw cid: src values found in img tags.
 */
export function getCidReferencesFromHtml(html: string): string[] {
  if (!html)
    return [];

  // Prefer DOMParser when available for accuracy
  try {
    if (globalThis.DOMParser !== undefined) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return [...doc.querySelectorAll('img[src]')]
        .map((img) => img.getAttribute('src') ?? '')
        .filter((src) => src.toLowerCase().startsWith('cid:'));
    }
  } catch {
    // fallthrough to regex
  }

  // Regex fallback for Node environments
  return [...html.matchAll(/<img\b[^>]*?\ssrc\s*=\s*(["'])(cid:[^"']+)\1/gi)].map((m) => m[2]);
}
